import { Box, HStack } from "@chakra-ui/react"
import { useRef } from "react"
import { useGalleryContext } from "./Gallery"
import { GridProps } from "./types"

const GalleryGrid = <T,>(props: GridProps<T>) => {
  const {
    items,
    itemsPerRow = 1,
    renderItem,
    gap,
    spacingX,
    spacingY,
    maxItemHeight
  } = props

  const gridContainerRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const itemRefs = useGalleryContext((v) => v.itemRefs)

  const normalizedItemsPerRow = Math.min(itemsPerRow, items.length)

  // The rather convoluated logic here is to ensure that items always fill the
  // available space in their row, even if the number of items is less than itemsPerRow.
  // This is made more complex because gridColumn: span <x> doesn't work with
  // fractional values, so we need to calculate the number of full rows and the
  // number of items in the last row separately.
  const rows = Math.ceil(items.length / normalizedItemsPerRow)
  const fullRows = rows - 1
  const lastRowStart = fullRows * itemsPerRow
  const fullRowsItems = items.slice(0, lastRowStart)
  const lastRowItems = items.slice(lastRowStart)

  return (
    <Box ref={gridContainerRef} className="gallery-container" pos="relative">
      <Box
        ref={gridRef}
        display="grid"
        className="gallery-grid"
        gridTemplateColumns={`repeat(${normalizedItemsPerRow}, 1fr)`}
        gridAutoRows={
          maxItemHeight ? `calc(${maxItemHeight} - ${gap}px)` : "min-content"
        }
        gap={`${gap}px`}
        paddingX={spacingX}
        paddingY={spacingY}
      >
        {fullRowsItems.map((item, index) => {
          return (
            <Box
              key={index}
              className="gallery-grid-item gallery-item-full-row"
              ref={(el: HTMLDivElement | null) =>
                (itemRefs.current[index] = el as HTMLDivElement)
              }
            >
              {renderItem(item, {
                index
              })}
            </Box>
          )
        })}
        {lastRowItems.length ? (
          <HStack
            gap="10px"
            w="100%"
            gridColumn={`span ${itemsPerRow}`}
            className="gallery-grid-last-row"
          >
            {lastRowItems.map((item, index) => (
              <Box
                key={index + lastRowStart}
                flex="1"
                className="gallery-grid-item gallery-item-last-row"
                h={
                  maxItemHeight
                    ? `calc(${maxItemHeight} - ${gap}px)`
                    : "min-content"
                }
                ref={(el: HTMLDivElement | null) =>
                  (itemRefs.current[index + lastRowStart] =
                    el as HTMLDivElement)
                }
              >
                {renderItem(item, {
                  index: index + lastRowStart
                })}
              </Box>
            ))}
          </HStack>
        ) : null}
      </Box>
    </Box>
  )
}

export default GalleryGrid
